/**
 * External data integration · CNPJ enrichment, CEP lookup, sanctions, feriados.
 *
 * The API surfaces four cooperating sources: BrasilAPI (primary CNPJ + CEP +
 * Feriados), OpenCNPJ (CNPJ fallback), and Portal da Transparência (CEIS +
 * CNEP sanctions). All shapes flow through these schemas — wire payloads
 * vary wildly between providers, so we normalize at the service layer
 * and expose a single tidy contract here.
 *
 * D1 caches the answers; the UI never sees provider-specific JSON.
 */
import { z } from 'zod';

/** A single QSA partner from CNPJ data — name + role only. */
export const cnpjPartnerSchema = z.object({
  nome: z.string(),
  qualificacao: z.string().nullable(),
});
export type CnpjPartner = z.infer<typeof cnpjPartnerSchema>;

/**
 * Normalized CNPJ enrichment payload. Built by `cnpj.service.ts` from
 * BrasilAPI primary or OpenCNPJ fallback. `source` tells the UI which
 * provider answered so we can show a tiny attribution caption.
 */
export const cnpjEnrichmentSchema = z.object({
  cnpj: z.string(),
  legalName: z.string(),
  tradeName: z.string().nullable(),
  /** ATIVA / BAIXADA / SUSPENSA / etc. — already normalized to uppercase. */
  status: z.string().nullable(),
  /** Primary CNAE description (atividade principal). */
  activity: z.string().nullable(),
  /** Single-line concatenated address for display. */
  address: z.string().nullable(),
  partners: z.array(cnpjPartnerSchema),
  source: z.enum(['brasilapi', 'opencnpj']),
  fetchedAt: z.string(),
});
export type CnpjEnrichment = z.infer<typeof cnpjEnrichmentSchema>;

/** ViaCEP-shaped result, lightly cleaned. */
export const cepResultSchema = z.object({
  cep: z.string(),
  logradouro: z.string(),
  bairro: z.string(),
  cidade: z.string(),
  estado: z.string(),
  ibge: z.string().nullable(),
});
export type CepResult = z.infer<typeof cepResultSchema>;

/**
 * One sanctions hit — either CEIS (impedidos) or CNEP (apenadas). Shape is
 * deliberately narrow: scoring happens in the service, the UI just
 * surfaces these rows verbatim under the supplier's compliance card.
 */
export const sanctionsHitSchema = z.object({
  source: z.enum(['ceis', 'cnep']),
  /** Sanctioning body (ente sancionador). */
  orgao: z.string().nullable(),
  /** Sanction description. */
  sancao: z.string().nullable(),
  /** ISO date or null. */
  dataInicio: z.string().nullable(),
  /** ISO date or null. */
  dataFim: z.string().nullable(),
});
export type SanctionsHit = z.infer<typeof sanctionsHitSchema>;

export const sanctionsResultSchema = z.object({
  cnpj: z.string(),
  isSanctioned: z.boolean(),
  hits: z.array(sanctionsHitSchema),
  checkedAt: z.string(),
});
export type SanctionsResult = z.infer<typeof sanctionsResultSchema>;

/** A single national or state holiday. */
export const feriadoSchema = z.object({
  date: z.string(),
  name: z.string(),
  type: z.string(),
});
export type Feriado = z.infer<typeof feriadoSchema>;

export const feriadosListSchema = z.object({
  year: z.number().int(),
  feriados: z.array(feriadoSchema),
  fetchedAt: z.string(),
});
export type FeriadosList = z.infer<typeof feriadosListSchema>;
